import { TEMPLATE_SOURCE_NONE, type TemplateForceTotalDto } from '@pams/shared';
import { BookletChip } from '../components/BookletChip';
import { MATRIX_COPY } from '../copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B.1 §2] THE FORCE MATRIX — the review site's own table: a row per source, a column per booklet
 *  column, totals on both edges, sources with the most people first.
 *
 *  ⚠️ IT DIVERGES FROM THE REVIEW SITE TWICE, AND SAYS SO HERE AND IN THE CHANGELOG. The review merged
 *  the women-police columns into one and ordered columns by rank; here each booklet column is its own,
 *  in the order the booklet first prints it, because each is its OWN DECISION — a merged "W/Police"
 *  header would hide which of them is still open.
 *
 *  ⚠️ A HEADER STILL OPEN READS AS THE INVERTED CHIP — the same "Not in PAMS" a person reads in the
 *  decisions panel, so a column that blocks is visible where its people are counted.
 *
 *  A plain `<table>` in a horizontal scroller: a cell never truncates, and the scroll is visible.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const ForceMatrix = ({
  totals,
  openColumns,
  openSources,
  caption,
  testId,
}: {
  totals: readonly TemplateForceTotalDto[];
  /** Booklet columns still undecided — their headers read as the inverted chip. */
  openColumns: ReadonlySet<string>;
  /** Source codes still undecided — their row labels do too. */
  openSources: ReadonlySet<string>;
  caption: string;
  testId: string;
}): React.ReactElement => {
  if (totals.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={testId}>
        {MATRIX_COPY.none}
      </p>
    );
  }
  const columns = [...new Set(totals.map((total) => total.column))];
  /* Source → column → people. Nested maps, never a joined key: a separator is a character some label may hold. */
  const cell = new Map<string, Map<string, number>>();
  const rowTotal = new Map<string, number>();
  const columnTotal = new Map<string, number>();
  for (const total of totals) {
    const row = cell.get(total.source) ?? new Map<string, number>();
    row.set(total.column, (row.get(total.column) ?? 0) + total.n);
    cell.set(total.source, row);
    rowTotal.set(total.source, (rowTotal.get(total.source) ?? 0) + total.n);
    columnTotal.set(total.column, (columnTotal.get(total.column) ?? 0) + total.n);
  }
  const firstSeen = [...new Set(totals.map((total) => total.source))];
  const sources = [...firstSeen].sort(
    (a, b) => (rowTotal.get(b) ?? 0) - (rowTotal.get(a) ?? 0) || firstSeen.indexOf(a) - firstSeen.indexOf(b),
  );
  const grand = [...rowTotal.values()].reduce((sum, value) => sum + value, 0);

  return (
    <figure className="flex flex-col gap-1.5" data-testid={testId}>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-muted text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 text-left font-medium">
                {MATRIX_COPY.source}
              </th>
              {columns.map((column) => (
                <th key={column} scope="col" className="px-3 py-2 text-right font-medium" data-column={column}>
                  {openColumns.has(column) ? (
                    <BookletChip variant="unmapped" title={MATRIX_COPY.openColumn}>
                      {column}
                    </BookletChip>
                  ) : (
                    column
                  )}
                </th>
              ))}
              <th scope="col" className="px-3 py-2 text-right font-medium">
                {MATRIX_COPY.total}
              </th>
            </tr>
          </thead>
          <tbody>
            {sources.map((source) => (
              <tr key={source} className="border-t border-border" data-source={source}>
                <th scope="row" className="px-3 py-1.5 text-left font-normal">
                  {source === TEMPLATE_SOURCE_NONE ? (
                    <span className="text-muted-foreground">{MATRIX_COPY.noSource}</span>
                  ) : openSources.has(source) ? (
                    <BookletChip variant="unmapped" title={MATRIX_COPY.openSource}>
                      {source}
                    </BookletChip>
                  ) : (
                    source
                  )}
                </th>
                {columns.map((column) => {
                  const value = cell.get(source)?.get(column);
                  return (
                    <td key={column} className="px-3 py-1.5 text-right">
                      {value === undefined ? <span className="text-subtle">·</span> : value}
                    </td>
                  );
                })}
                <td className="px-3 py-1.5 text-right font-medium">{rowTotal.get(source) ?? 0}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-border font-medium">
            <tr>
              <th scope="row" className="px-3 py-1.5 text-left">
                {MATRIX_COPY.total}
              </th>
              {columns.map((column) => (
                <td key={column} className="px-3 py-1.5 text-right">
                  {columnTotal.get(column) ?? 0}
                </td>
              ))}
              <td className="px-3 py-1.5 text-right">{grand}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <figcaption className="text-xs text-muted-foreground">{caption}</figcaption>
    </figure>
  );
};
